import React, { useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  MenuItem,
  Box,
  Alert,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { postSupportCommand, uploadMenuFile } from 'apis/supportAPIs';
import ConfirmMerchantModal from './ConfirmMerchantModal';

const useStyles = makeStyles((theme) => ({
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  iconRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  actions: {
    marginTop: 'auto',
    padding: theme.spacing(2),
  },
  fileInput: {
    marginTop: theme.spacing(1),
  },
}));

const SEQUENCES = ['first', 'second', 'third', 'fourth', 'fifth'];

const ImportMenu = ({ restaurantId, deviceId, merchantName, onCommandStart }) => {
  const classes = useStyles();
  const [mode, setMode] = useState('pull');
  const [sequence, setSequence] = useState('first');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0];
    if (selected) {
      setFile(selected);
      setUploadError(null);
    }
  };

  const isValid = mode === 'pull' || (mode === 'upload' && file);

  const handleRunClick = () => {
    setConfirmOpen(true);
  };

  const handleConfirm = useCallback(async () => {
    setConfirmOpen(false);
    setLoading(true);
    setUploadError(null);

    try {
      let source;
      if (mode === 'upload' && file) {
        const uploadResult = await uploadMenuFile(file);
        source = uploadResult.storagePath;
      }

      const params =
        mode === 'pull'
          ? { source: 'server', sequence }
          : { source };

      const { commandId } = await postSupportCommand({
        restaurantId,
        deviceId,
        type: 'importMenu',
        params,
      });
      onCommandStart(commandId, 'importMenu');
    } catch (err) {
      setUploadError(err.response?.data?.message || err.message || 'Import failed');
      onCommandStart(null, 'importMenu');
    } finally {
      setLoading(false);
    }
  }, [mode, file, sequence, restaurantId, deviceId, onCommandStart]);

  return (
    <>
      <Card className={classes.card} aria-label="Import Menu" role="region">
        <CardContent>
          <div className={classes.iconRow}>
            <UploadFileIcon color="primary" />
            <Typography variant="h6">Import Menu</Typography>
          </div>

          <Alert severity="warning" sx={{ mb: 2 }}>
            This overwrites the merchant&apos;s on-device menu.
          </Alert>

          <Typography variant="body2" gutterBottom>
            Source
          </Typography>
          <ToggleButtonGroup
            value={mode}
            exclusive
            onChange={(_, v) => v && setMode(v)}
            size="small"
            sx={{ mb: 2 }}
            aria-label="Import source"
          >
            <ToggleButton value="pull" aria-label="Pull from server">
              Pull from server
            </ToggleButton>
            <ToggleButton value="upload" aria-label="Upload JSON file">
              Upload JSON file
            </ToggleButton>
          </ToggleButtonGroup>

          {mode === 'pull' && (
            <Box sx={{ mt: 1 }}>
              <TextField
                select
                label="Sequence"
                value={sequence}
                onChange={(e) => setSequence(e.target.value)}
                size="small"
                fullWidth
                inputProps={{ 'aria-label': 'Menu sequence' }}
              >
                {SEQUENCES.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          )}

          {mode === 'upload' && (
            <Box className={classes.fileInput}>
              <Button
                variant="outlined"
                component="label"
                fullWidth
              >
                {file ? file.name : 'Choose JSON file'}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json,application/json"
                  hidden
                  onChange={handleFileChange}
                  aria-label="Upload menu JSON file"
                />
              </Button>
            </Box>
          )}

          {uploadError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {uploadError}
            </Alert>
          )}
        </CardContent>
        <CardActions className={classes.actions}>
          <Button
            variant="contained"
            color="warning"
            onClick={handleRunClick}
            disabled={!isValid || loading}
            fullWidth
          >
            {loading ? 'Processing…' : 'Run'}
          </Button>
        </CardActions>
      </Card>

      <ConfirmMerchantModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirm}
        merchantName={merchantName}
        loading={loading}
      />
    </>
  );
};

ImportMenu.propTypes = {
  restaurantId: PropTypes.string.isRequired,
  deviceId: PropTypes.string.isRequired,
  merchantName: PropTypes.string.isRequired,
  onCommandStart: PropTypes.func.isRequired,
};

export default ImportMenu;
